import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  ClickAwayListener,
  useMediaQuery,
  CircularProgress,
  Theme,
  IconButton,
} from '@mui/material';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { palette } from '@/theme/constants';
import {
  getExtraContent,
  getMemories,
  getStorageLogs,
  getUserPurchases,
  openSubscriptionPopup,
  refreshUserData,
  removeMemory,
  setCreateMemoryStep,
  showActualSection,
  showPopup,
} from '@/store/actions';
import { authSelector, extrasSelector, homeSelector, memorySelector, purchaseSelector } from '@/store/selectors';
import {
  canCreateMemory,
  cdn_url,
  checkPermissions,
  checkRoleAndPermission,
  ExtractCallbackType,
  GetInitials,
  hasUserPurchasedTheStory,
  makeHasStoryAccess,
  promisifiedCallback,
} from '@/utils';

import { FilterDropdown, MuiButton, RtfComponent } from '@/components';
import { SortDropdown } from '@/components/SortDropdown';
import { ViewedControlsDropdown } from '@/components/ViewedControlsDropdown';
import Image1Icon from '../../public/icons/image1';
import Video1Icon from '../../public/icons/video1';
import Text1Icon from '../../public/icons/test1';
import Audio1Icon from '../../public/icons/audio1';
import Audio2Icon from '../../public/icons/audioGradient';
import PopupModal from './PayWallModal';
import VideoThumbnail from './VideoThumbnail';
import SearchGrid from './AppBar/SearchGrid';
import { DeleteMemoryModal, MemoryDetail } from '@/screens/Memories/components';
import { UseFirstRender, UseIntermitence, useViewedMemories } from '@/hooks';
import { useAuth } from '@/hooks/authContext';

// -------------------- Types --------------------

type MediaType = 'image' | 'audio' | 'video' | 'text';

interface MediaItem {
  id?: any;
  extendedPalette: any;
  type: MediaType | 'create-memory' | '';
  src?: string;
  asset?: string;
  preview_asset?: string;
  alt?: string;
  content?: string;
  username?: string;
  userImage?: string;
  title?: string;
  user?: any;
  user_id?: any;
  created_at?: string;
  is_pinned?: boolean;
  memory_details?: {
    complementaryImage?: string[];
    complementaryAudio?: string[];
    complementaryVideo?: string[];
    complementaryText?: string[];
  };
  approved?: boolean;
}

interface MediaGridProps {
  invitation?: boolean;
  story?: any;
  extendedPalette?: any;
  actionSuccessColab?: any;
  NoColab?: any;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
}

//-------------------- Helpers --------------------

function useDebouncedValue<T>(value: T, delay = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

// Pinterest-style Masonry Component
interface PinterestMasonryProps {
  children: React.ReactNode[];
  columnCount: number;
  gap?: number;
}

const PinterestMasonry: React.FC<PinterestMasonryProps> = React.memo(function PinterestMasonry({
  children,
  columnCount,
  gap = 8,
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const [positions, setPositions] = useState<Map<number, { left: number; top: number }>>(new Map());
  const [animationDelays, setAnimationDelays] = useState<Map<number, number>>(new Map());
  const [containerHeight, setContainerHeight] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const columnHeightsRef = useRef<number[]>([]);
  const calculatedIndicesRef = useRef<Set<number>>(new Set());

  // Measure container width
  useLayoutEffect(() => {
    if (!containerRef.current) return;

    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(containerRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  // Calculate column width based on container width
  const columnWidth = containerWidth > 0
    ? (containerWidth - (columnCount - 1) * gap) / columnCount
    : 0;

  // Calculate positions for items
  useLayoutEffect(() => {
    if (!containerRef.current || children.length === 0 || columnWidth === 0) return;

    const newPositions = new Map(positions);
    const newDelays = new Map(animationDelays);
    const columnHeights = columnHeightsRef.current.length === columnCount
      ? [...columnHeightsRef.current]
      : Array(columnCount).fill(0);

    let hasChanges = false;
    let newItemCount = 0;

    // Process each child
    children.forEach((_, index) => {
      // Skip if already calculated and element exists
      if (calculatedIndicesRef.current.has(index) && newPositions.has(index)) {
        return;
      }

      const itemEl = itemRefs.current.get(index);
      if (!itemEl) return;

      // Find shortest column
      let shortestCol = 0;
      let shortestHeight = columnHeights[0];
      for (let i = 1; i < columnCount; i++) {
        if (columnHeights[i] < shortestHeight) {
          shortestHeight = columnHeights[i];
          shortestCol = i;
        }
      }

      // Calculate position
      const left = shortestCol * (columnWidth + gap);
      const top = columnHeights[shortestCol];

      newPositions.set(index, { left, top });

      // Set stagger delay for this new item
      newDelays.set(index, newItemCount * 0.1);
      newItemCount++;

      calculatedIndicesRef.current.add(index);
      hasChanges = true;

      // Update column height
      const itemHeight = itemEl.getBoundingClientRect().height;
      columnHeights[shortestCol] = top + itemHeight + gap;
    });

    columnHeightsRef.current = columnHeights;

    if (hasChanges) {
      setPositions(newPositions);
      setAnimationDelays(newDelays);
      setContainerHeight(Math.max(...columnHeights));
    }
  }, [children.length, columnCount, gap, columnWidth, positions, animationDelays]);

  // Reset when column count or container width changes
  useEffect(() => {
    calculatedIndicesRef.current.clear();
    columnHeightsRef.current = [];
    setPositions(new Map());
    setAnimationDelays(new Map());
  }, [columnCount, containerWidth]);

  // Observe resize of items to recalculate if needed
  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      let needsRecalc = false;
      entries.forEach((entry) => {
        const index = parseInt(entry.target.getAttribute('data-index') || '-1');
        if (index >= 0 && calculatedIndicesRef.current.has(index)) {
          // Item size changed after initial calculation - need full recalc
          needsRecalc = true;
        }
      });

      if (needsRecalc) {
        // Debounce recalculation
        calculatedIndicesRef.current.clear();
        columnHeightsRef.current = [];
        setPositions(new Map());
        setAnimationDelays(new Map());
      }
    });

    itemRefs.current.forEach((el) => {
      if (el) resizeObserver.observe(el);
    });

    return () => resizeObserver.disconnect();
  }, [children.length]);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        width: '100%',
        height: containerHeight,
      }}
    >
      {children.map((child, index) => {
        const pos = positions.get(index);
        const staggerDelay = animationDelays.get(index) ?? 0;

        return (
          <Box
            key={index}
            ref={(el: HTMLDivElement | null) => {
              if (el) itemRefs.current.set(index, el);
              else itemRefs.current.delete(index);
            }}
            data-index={index}
            sx={{
              position: 'absolute',
              width: columnWidth,
              left: pos?.left ?? 0,
              top: pos?.top ?? 0,
              opacity: pos ? 1 : 0,
              transform: pos ? 'translateY(0)' : 'translateY(10px)',
              transition: `opacity 0.15s ease-out ${staggerDelay}s, transform 0.15s ease-out ${staggerDelay}s`,
            }}
          >
            {child}
          </Box>
        );
      })}
    </Box>
  );
});

const CreateCard: React.FC<{
  isMobile: boolean;
  extendedPalette: any;
  label: string;
  onClick: () => void;
}> = React.memo(function CreateCard({ isMobile, extendedPalette, label, onClick }) {
  return (
    <Paper
      onClick={onClick}
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        backgroundColor: '#FEFEFE40',
        color: extendedPalette.cardMediaColor,
        display: 'flex',
        flexDirection: 'column',
        border: '1px dashed rgba(217, 217, 217, 1)',
        cursor: 'pointer',
        justifyContent: 'center',
        alignItems: 'center',
        height: isMobile ? '210px' : '320px',
        width: isMobile ? '158px' : '250px',
        marginBottom: isMobile ? '2rem' : '2.1rem',
      }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <IconButton
          sx={{
            width: isMobile ? 40 : 50,
            height: isMobile ? 40 : 50,
            backgroundColor: 'white',
            '&:hover': { backgroundColor: extendedPalette.cardIconHoverColor },
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '50%',
            boxShadow: 3,
          }}
          onClick={onClick}>
          <Image
            src={'/icons/add.svg'}
            width={isMobile ? 20 : 26}
            height={isMobile ? 20 : 26}
            quality={80}
            alt={'add'}
          />
        </IconButton>
        <Typography fontSize={'20px'} align='center' mt={'1rem'} px={'6rem'} textAlign={'center'}>
          {label}
        </Typography>
      </Box>
    </Paper>
  );
});

const MediaGrid: React.FC<MediaGridProps> = ({
  story,
  extendedPalette = {},
  actionSuccessColab,
  NoColab,
  invitation,
  scrollContainerRef,
}) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const { setRedirectTo } = useAuth();
  const isMobile = useMediaQuery((template: Theme) => template.breakpoints.down('md'));
  const isSmallScreen = useMediaQuery('(max-width:600px)');
  const { t } = useTranslation();

  const { actionSuccess, memoriesLoaded } = useSelector(memorySelector);
  const extras = useSelector(extrasSelector);
  const purchase = useSelector(purchaseSelector);
  const { user, isAuth } = useSelector(authSelector);
  const homeData = useSelector(homeSelector);

  const [openMemoryModal, setOpenMemoryModal] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<MediaItem | null>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'All' | MediaType>('All');
  const [Typevalue, setTypeValue] = useState('');
  const [openFilters, setOpenFilters] = useState(false);
  const [showFour, setShowFour] = useState(false);
  const [isChronological, setIsChronological] = useState(story?.isChronological || false);
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('oldest');
  const [visibleItems, setVisibleItems] = useState(10);
  const [modalOpen, setModalOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [masonryKey, setMasonryKey] = useState(0); // Key to force Masonry remount when needed
  const loadMoreButtonRef = useRef<HTMLSpanElement>(null);
  const isLoadingRef = useRef(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isScrollingRef = useRef(false);

  const { status: deleteStatusMemory, switchStatus: switchDeleteMemory } = UseIntermitence();
  const { status, switchStatus } = UseIntermitence();

  const debouncedSearch = useDebouncedValue(search.trim().toLowerCase(), 250);

  // Viewed memories tracking
  const { isMemoryViewed, markAsViewed, markAllAsViewed, markAllAsUnviewed } = useViewedMemories(story?.id);

  const hasAccess = useMemo(
    () => makeHasStoryAccess(user, story, purchase?.userPurchases),
    [user?.id, user?.roles, story?.id, story?.user_id, purchase?.userPurchases],
  );

  const userStoryRoleMemo = useMemo(
    () => user?.roles?.find((r: any) => r.story_id === story?.id),
    [user?.roles, story?.id],
  );

  // -------------------- Effects --------------------

  // Reset state when story changes
  useEffect(() => {
    setVisibleItems(10); // Reset visible items count
    setMasonryKey((k) => k + 1); // Force Masonry remount
  }, [story?.id]);

  useEffect(() => {
    if (story?.id || invitation) {
      dispatch(getMemories({ story_id: story?.id, invitation }));
      if (router.query?.id) dispatch(getExtraContent(router.query?.id as string));
      if (user?.id) dispatch(getUserPurchases(user.id));
    }
  }, [story?.id, invitation, step]);

  useEffect(() => {
    if (user?.id) dispatch(getUserPurchases(user.id));
  }, [dispatch, user?.id]);

  useEffect(() => {
    const isPaid = extras?.extraContent?.isPaid;
    if (typeof isPaid === 'boolean') {
      setShowFour(isPaid && !(isAuth && hasAccess));
    }
  }, [extras?.extraContent?.isPaid, isAuth, hasAccess]);

  UseFirstRender(() => {
    if (router?.query?.memoryId && memoriesLoaded) {
      const memory = memoriesLoaded.find((m: any) => Number(m?.id) === Number(router?.query?.memoryId));
      if (memory) {
        setSelectedMedia(memory);
        switchStatus();
      }
    }
  }, [router?.query]);

  useEffect(() => {
    if (story?.url === router.query?.id) {
      const href = router?.query?.memoryId
        ? `/app/story/${story?.url}?memoryId=${router?.query?.memoryId}`
        : `/app/story/${story?.url}`;
      router.push(href, undefined, { shallow: true });
    }
  }, [router.query?.id, story?.url]);

  // Close detail resets selected state
  useEffect(() => {
    if (!openMemoryModal) setSelectedMedia(null);
  }, [openMemoryModal]);

  // -------------------- Callbacks --------------------

  const handleCloseFilters = useCallback(() => setOpenFilters(false), []);

  const handleOpenModal = useCallback((item: MediaItem) => {
    setSelectedMedia(item);
    setOpenMemoryModal(true);
  }, []);

  const closeMemory = useCallback(() => {
    setOpenMemoryModal(false);
    setSelectedMedia(null);
    if (story?.url) router.push(`/app/story/${story?.url}`, undefined, { shallow: true });
    switchStatus();
  }, [router, story?.url, switchStatus]);

  const deleteMemory = useCallback(async () => {
    setOpenMemoryModal(false);
    setSelectedMedia(null);
    switchDeleteMemory();
    dispatch(showPopup());
    if (story?.url) router.push(`/app/story/${story?.url}`, undefined, { shallow: true });
    const { callback, promise } = promisifiedCallback<ExtractCallbackType<typeof removeMemory>>();
    dispatch(removeMemory({ id: selectedMedia?.id, story_id: story?.id }, callback));
    const { ok } = await promise;
    if (ok && user?.id) dispatch(getStorageLogs(user.id));
  }, [dispatch, router, story?.id, story?.url, switchDeleteMemory, selectedMedia?.id, user?.id]);

  const goToCreateMemory = useCallback(() => {
    if (!user?.token) {
      if (story?.url) {
        setRedirectTo(`/app/story/${story.url}/memory/create`);
      }
      router.push('/app/register');
      return;
    }

    dispatch(refreshUserData());
    if (story?.user_id === user?.id) {
      const ownerHasPerm = checkRoleAndPermission(
        story?.user?.roles,
        'Subscriber_Individual',
        'CLIENT_MEMORY_UPDATE',
        story?.user_id,
      );
      if (!ownerHasPerm) {
        localStorage.setItem('router_history', `/app/story/${story?.url}`);
        setTimeout(() => dispatch(openSubscriptionPopup()), 2000);
        return;
      }
    }
    if (story?.url) {
      router.push(`/app/story/${story?.url}/memory/create`);
      dispatch(setCreateMemoryStep(1));
      dispatch(showActualSection('create_new_memory'));
    }
  }, [dispatch, router, setRedirectTo, story, user?.id, user?.token]);

  const AllowOpenModel = useCallback(
    (item: MediaItem) => {
      const isPaid = Boolean(story?.isPaid);
      // Free story: open for all
      if (!isPaid) {
        handleOpenModal(item);
        if (item?.id) markAsViewed(item.id); // Mark as viewed
        if (story?.url) router.push(`/app/story/${story?.url}?memoryId=${item?.id}`, undefined, { shallow: true });
        return;
      }
      // Paid story: need auth + access
      if (isAuth && hasAccess) {
        handleOpenModal(item);
        if (item?.id) markAsViewed(item.id); // Mark as viewed
        if (story?.url) router.push(`/app/story/${story?.url}?memoryId=${item?.id}`, undefined, { shallow: true });
      } else {
        setModalOpen(true);
      }
    },
    [hasAccess, handleOpenModal, isAuth, router, story?.isPaid, story?.url, markAsViewed],
  );

  const AllowHandleLoadMore = useCallback(() => {
    const isPaid = extras?.extraContent?.isPaid;
    if (isPaid && !(isAuth && hasAccess)) {
      setModalOpen(true);
      return;
    }
    setVisibleItems((v) => v + 10);
  }, [extras?.extraContent?.isPaid, hasAccess, isAuth]);

  const handleCheckTypes = useCallback(
    (value: any) => {
      if (value === undefined) return;
      setTypeValue(value);
      const updated = [...(homeData?.criterias?.types || [])];
      const idx = updated.findIndex((v) => v === value);
      if (idx > -1) updated.splice(idx, 1);
      else updated.push(value);
      // If needed, dispatch to store here
    },
    [homeData?.criterias?.types],
  );

  // Handlers for viewed memory controls
  const handleMarkAllAsViewed = useCallback(() => {
    const allMemoryIds = (memoriesLoaded as MediaItem[])?.map((m) => m.id).filter(Boolean) || [];
    markAllAsViewed(allMemoryIds);
  }, [memoriesLoaded, markAllAsViewed]);

  const handleMarkAllAsUnviewed = useCallback(() => {
    markAllAsUnviewed();
  }, [markAllAsUnviewed]);

  // -------------------- Derived Lists --------------------

  const matchesType = useCallback(
    (item: MediaItem) => {
      if (Typevalue === 'all' || Typevalue === '' || item.id === 'create-memory-card') return true;
      return item.user?.roles?.some((r: any) => r.user_type === Typevalue);
    },
    [Typevalue],
  );

  const filteredMediaItems: MediaItem[] = useMemo(() => {
    if (!memoriesLoaded) return [] as MediaItem[];
    const list = (memoriesLoaded as MediaItem[]).filter((item) => {
      const isCreatorOrOwner =
        item.user_id === user?.id ||
        story?.user_id === user?.id ||
        user?.roles?.some((r: any) => r.story_id === story?.id && r.role.name === 'Story_Admin');

      const hasItemAccess = item.approved || (!item.approved && isCreatorOrOwner);

      const passesFilter = filter === 'All' || item.type?.toLowerCase() === filter.toLowerCase();

      const passesSearch =
        !debouncedSearch ||
        (debouncedSearch.length >= 3 &&
          ((item.title || '').toLowerCase().includes(debouncedSearch) ||
            (item.username || '').toLowerCase().includes(debouncedSearch)));

      return hasItemAccess && passesFilter && passesSearch && matchesType(item);
    });

    list.sort((a: any, b: any) => {
      // Keep pinned memories at the top
      if (a.is_pinned && !b.is_pinned) return -1;
      if (!a.is_pinned && b.is_pinned) return 1;

      // Sort by date based on sortOrder
      const da = new Date(a.created_at || 0).getTime();
      const db = new Date(b.created_at || 0).getTime();
      return sortOrder === 'newest' ? db - da : da - db;
    });

    return list;
  }, [
    debouncedSearch,
    filter,
    sortOrder,
    matchesType,
    memoriesLoaded,
    story?.id,
    story?.user_id,
    user?.id,
    user?.roles,
  ]);

  const mediaItemsWithCreateCard: MediaItem[] = useMemo(() => {
    return [{ id: 'create-memory-card', type: 'create-memory' } as MediaItem, ...filteredMediaItems];
  }, [filteredMediaItems]);

  const allItemsLoaded = useMemo(
    () => visibleItems >= mediaItemsWithCreateCard.length,
    [visibleItems, mediaItemsWithCreateCard.length],
  );

  // Generate a stable key for Masonry based on item count to force remount when items are deleted
  const masonryItemCount = filteredMediaItems.length;

  // Force Masonry remount when items are deleted (item count decreases)
  const prevItemCountRef = useRef(masonryItemCount);
  useEffect(() => {
    if (masonryItemCount < prevItemCountRef.current) {
      // Items were deleted - force Masonry remount
      setMasonryKey((k) => k + 1);
    }
    prevItemCountRef.current = masonryItemCount;
  }, [masonryItemCount]);

  // Track scrolling state to avoid loading during active scroll
  useEffect(() => {
    const handleScroll = () => {
      isScrollingRef.current = true;

      // Clear existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Set a new timeout to mark scrolling as stopped
      scrollTimeoutRef.current = setTimeout(() => {
        isScrollingRef.current = false;
      }, 150); // Wait 150ms after last scroll event
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Auto-load effect with Intersection Observer
  useEffect(() => {
    if (!loadMoreButtonRef.current || allItemsLoaded || showFour) {
      return;
    }

    const buttonRef = loadMoreButtonRef.current;
    // On mobile, the page scrolls (not the container), so use viewport (null) as root
    // On desktop, the container scrolls, so use the container ref as root
    const scrollRoot = isMobile ? null : (scrollContainerRef?.current || null);

    // Create an intersection observer that triggers when button is near viewport
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Only trigger when button is visible, not already loading, AND not actively scrolling
          if (entry.isIntersecting && !isLoadingRef.current && !isScrollingRef.current) {
            isLoadingRef.current = true;

            setVisibleItems((v) => v + 10);

            // Reset loading flag after delay to prevent rapid re-triggering
            setTimeout(() => {
              isLoadingRef.current = false;
            }, 500);
          }
        });
      },
      {
        // On mobile use viewport (null), on desktop use the scroll container
        root: scrollRoot,
        // Trigger when button is 200px from entering the scroll container's visible area
        rootMargin: '0px 0px 400px 0px',
        threshold: 0,
      }
    );

    observer.observe(buttonRef);

    return () => {
      observer.disconnect();
    };
  }, [allItemsLoaded, showFour, scrollContainerRef, isMobile]);

  // -------------------- Icon size helpers --------------------
  const sizeText = isSmallScreen ? '25px' : '30px';
  const sizeImage = isSmallScreen ? '25px' : '40px';
  const sizeVideo = isSmallScreen ? '25px' : '40px';
  const sizeAudio = isSmallScreen ? '25px' : '40px';

  const getIcon = useCallback(
    (type: string, color: string) => {
      switch (type) {
        case 'image':
          return <Image1Icon color={color} size={sizeImage} />;
        case 'video':
          return <Video1Icon color={color} size={sizeVideo} />;
        case 'audio':
          return <Audio1Icon color={color} size={sizeAudio} />;
        case 'text':
          return <Text1Icon color={color} size={sizeText} />;
        default:
          return null;
      }
    },
    [sizeAudio, sizeImage, sizeText, sizeVideo],
  );

  // -------------------- Render --------------------

  return (
    <>
      <Box sx={{ width: '100%', maxWidth: '1200px', margin: '0 auto', paddingBottom: '150px' }}>
        {/* Top bar */}
        {!isMobile && (actionSuccessColab || NoColab) && (
          <Box
            sx={{
              padding: '10px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
              background: 'rgba(255,255,255,0.05)',
              backdropFilter: 'blur(20px)',
              flexDirection: 'row',
              position: 'sticky',
              borderRadius: '10px',
              zIndex: '998',
              margin: 0,
              top: '6rem',
            }}>
            <SearchGrid
              color={extendedPalette.hasCustomTheme ? extendedPalette.storyBackground : '#131544'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClear={() => setSearch('')}
              sx={{
                width: { xs: '12rem', sm: '20rem' },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { border: 'none', color: 'white' },
                  '& input': { color: 'white' },
                },
                '& input:-webkit-autofill': {
                  WebkitTextFillColor: 'white',
                  transition: 'background-color 5000s ease-in-out 0s',
                },
              }}
            />

            <ClickAwayListener onClickAway={handleCloseFilters} disableReactTree>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: { xs: 'flex-end', sm: 'flex-end' },
                  flexGrow: 1,
                  flexWrap: 'nowrap',
                  gap: '8px',
                  padding: '0 5px 0 0',
                }}>
                <FilterDropdown handleCheckTypes={handleCheckTypes} extendedPalette={extendedPalette} listItem={'7'} />
                <SortDropdown
                  sortOrder={sortOrder}
                  onSortChange={setSortOrder}
                  extendedPalette={extendedPalette}
                />
                <ViewedControlsDropdown
                  onMarkAllViewed={handleMarkAllAsViewed}
                  onMarkAllUnviewed={handleMarkAllAsUnviewed}
                  extendedPalette={extendedPalette}
                />
              </Box>
            </ClickAwayListener>
          </Box>
        )}

        {/* Memories header (desktop only when allowed) */}
        {(actionSuccessColab || NoColab) &&
          actionSuccess &&
          memoriesLoaded?.length > 0 &&
          !story?.isLocked &&
          (checkPermissions(user?.roles || [], 'CLIENT_MEMORY_CREATE', story?.id) || user?.id === story?.user_id) && (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '1rem 16px 0px 40px',
                zIndex: 1,
              }}>
              <Typography variant='h6' sx={{ fontSize: '20px', color: 'white', zIndex: 2 }}>
                Memories
              </Typography>
              {isMobile && (
                <Box sx={{ display: 'flex', gap: '8px' }}>
                  <FilterDropdown handleCheckTypes={handleCheckTypes} extendedPalette={extendedPalette} listItem={'7'} />
                  <SortDropdown
                    sortOrder={sortOrder}
                    onSortChange={setSortOrder}
                    extendedPalette={extendedPalette}
                  />
                  <ViewedControlsDropdown
                    onMarkAllViewed={handleMarkAllAsViewed}
                    onMarkAllUnviewed={handleMarkAllAsUnviewed}
                    extendedPalette={extendedPalette}
                  />
                </Box>
              )}
            </Box>
          )}

        {/* No results for selected Typevalue */}
        {Typevalue && filteredMediaItems.length === 0 && (
          <Box ml={'2.5rem'} mt={'1rem'}>
            <Typography fontSize={'1rem'}>No memories found for: {Typevalue}</Typography>
          </Box>
        )}

        {/* Media Grid & Empty states */}
        {(actionSuccessColab || NoColab) &&
        actionSuccess &&
        ((memoriesLoaded?.length ?? 0) > 0 || mediaItemsWithCreateCard.length > 0) ? (
          <Box
            key={masonryKey}
            sx={{
              position: 'relative',
              mt: '1rem',
              padding: isMobile ? '0 0.6rem 0 0rem' : 0,
              zIndex: '900',
              paddingLeft: '20px',
            }}>
            <PinterestMasonry
              columnCount={isMobile ? 2 : 4}
              gap={8}
            >
            {mediaItemsWithCreateCard.slice(0, showFour ? 4 : visibleItems).map((item: MediaItem, index: number) => {
              // Use item ID for stable key
              const itemKey = String(item.id);

              return (
              <Box
                key={itemKey}
                sx={{
                  width: '100%',
                }}>
                {item.type === 'create-memory' ? (
                  <CreateCard
                    isMobile={isMobile}
                    extendedPalette={extendedPalette}
                    label={memoriesLoaded?.length === 0 ? t('first_memory') : t('create_memory')}
                    onClick={goToCreateMemory}
                  />
                ) : (
                  <Box
                    onClick={() => AllowOpenModel(item)}
                    sx={{
                      borderRadius: 2,
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                      cursor: 'pointer',
                      marginBottom: 0.5,
                      // Opacity: viewed items at 70%, full opacity on hover
                      opacity: isMemoryViewed(item?.id) ? 0.7 : 1,
                      transition: 'opacity 0.2s ease-in-out',
                      '&:hover': {
                        opacity: 1,
                      },
                    }}>
                    {/* Media */}
                    <Box sx={{ position: 'relative', borderRadius: '12px' }}>
                      {/* Pinned Icon */}
                      {item.is_pinned && (
                        <Box sx={{ position: 'absolute', top: 5, left: 5, backgroundColor: 'transparent', zIndex: 2 }}>
                          <IconButton sx={{ backgroundColor: 'transparent' }}>
                            <Image src='/icons/pinned.svg' alt='Pinned' width={20} height={20} />
                          </IconButton>
                        </Box>
                      )}
                      {item.type === 'image' && (
                        <Box sx={{ position: 'relative', display: 'inline-block' }}>
                          {/* Blue Dot Badge for image */}
                          {!isMemoryViewed(item?.id) && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                background: 'linear-gradient(148deg,rgba(109, 181, 252, 1) 0%, rgba(0, 122, 255, 1) 100%)',
                                width: '16px',
                                height: '16px',
                                borderRadius: '50%',
                                zIndex: 10,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                              }}
                            />
                          )}
                          <Image
                            src={`${cdn_url}${item.asset}`}
                            alt={item.title || 'image'}
                            width={isMobile ? 158 : 250}
                            height={isMobile ? 210 : 320}
                            loading={index < 10 ? 'eager' : 'lazy'}
                            style={{ objectFit: 'cover', borderRadius: '12px' }}
                          />
                        </Box>
                      )}

                      {item.type === 'video' && (
                        <Box
                          sx={{
                            backgroundColor: extendedPalette.cardMediaBackground,
                            borderRadius: 2,
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            minHeight: '202px',
                            width: isMobile ? 158 : 250,
                            padding: 2,
                            position: 'relative',
                          }}>
                          {/* Blue Dot Badge for video */}
                          {!isMemoryViewed(item?.id) && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                background: 'linear-gradient(148deg,rgba(109, 181, 252, 1) 0%, rgba(0, 122, 255, 1) 100%)',
                                width: '16px',
                                height: '16px',
                                borderRadius: '50%',
                                border: '1px solid #0458B3',
                                zIndex: 10,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                              }}
                            />
                          )}
                          <VideoThumbnail
                            extendedPalette={extendedPalette}
                            item={
                              item?.memory_details?.complementaryVideo
                                ? item?.memory_details?.complementaryVideo?.[0]
                                : item
                            }
                          />
                        </Box>
                      )}

                      {item.type === 'audio' && (
                        <Box
                          sx={{
                            backgroundColor: extendedPalette.cardMediaBackground,
                            borderRadius: 2,
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            height: '202px',
                            width: isMobile ? 158 : 250,
                            padding: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                          }}>
                          {/* Blue Dot Badge for audio */}
                          {!isMemoryViewed(item?.id) && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                background: 'linear-gradient(148deg,rgba(109, 181, 252, 1) 0%, rgba(0, 122, 255, 1) 100%)',
                                width: '16px',
                                height: '16px',
                                borderRadius: '50%',
                                border: '1px solid #0458B3',
                                zIndex: 10,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                              }}
                            />
                          )}
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '5px',
                              width: '100%',
                            }}>
                            <Box sx={{ position: 'relative', cursor: 'pointer', width: '35px' }}>
                              <Image src={'/icons/playbut.svg'} alt={'icon'} width={35} height={35} />
                            </Box>
                            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                              <Audio2Icon
                                color={extendedPalette.audioGradientColor2}
                                color2={extendedPalette.audioGradientColor2}
                              />
                            </Box>
                          </Box>
                        </Box>
                      )}

                      {item.type === 'text' && (
                        <Box
                          sx={{
                            backgroundColor: extendedPalette.cardMediaBackground,
                            borderRadius: 2,
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            padding: 2,
                            minHeight: '150px',
                            maxHeight: '202px',
                            width: isMobile ? '158px' : '250px',
                            overflow: 'hidden',
                            position: 'relative',
                          }}>
                          {/* Blue Dot Badge for text */}
                          {!isMemoryViewed(item?.id) && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                background: 'linear-gradient(148deg,rgba(109, 181, 252, 1) 0%, rgba(0, 122, 255, 1) 100%)',
                                width: '16px',
                                height: '16px',
                                borderRadius: '50%',
                                border: '1px solid #0458B3',
                                zIndex: 10,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                              }}
                            />
                          )}
                          <Box sx={{ height: '100%', overflow: 'hidden' }}>
                            <RtfComponent rtf={item?.memory_details?.complementaryText} label={'p'} />
                            <Box
                              sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                width: '100%',
                                height: '80px',
                                backgroundColor: extendedPalette.cardMediaBackground,
                                opacity: 0.8,
                              }}
                            />
                          </Box>
                        </Box>
                      )}

                      {item?.type === '' && (
                        <Box
                          sx={{
                            backgroundColor: extendedPalette.cardMediaBackground,
                            borderRadius: 2,
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            padding: 2,
                            height: '150px',
                            width: isMobile ? '158px' : '250px',
                            overflow: 'hidden',
                            position: 'relative',
                          }}>
                          {/* Blue Dot Badge for empty type */}
                          {!isMemoryViewed(item?.id) && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                backgroundColor: '#0072CE',
                                width: '16px',
                                height: '16px',
                                borderRadius: '50%',
                                border: '1px solid #0458B3',
                                zIndex: 10,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                              }}
                            />
                          )}
                          <Box sx={{ height: '100%', overflow: 'hidden' }}>
                            <RtfComponent rtf={item?.memory_details?.complementaryText} label={'p'} />
                            <Box
                              sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                width: '100%',
                                height: '40px',
                                WebkitMaskImage: 'linear-gradient(to bottom, black 70%, transparent 100%)',
                                maskImage: 'linear-gradient(to bottom, black 70%, transparent 100%)',
                              }}
                            />
                          </Box>
                        </Box>
                      )}

                      {/* Avatar */}
                      <Box sx={{ position: 'absolute', bottom: 10, left: 10 }}>
                        <Box sx={{ position: 'relative', display: 'inline-block' }}>
                          <Avatar
                            src={item?.user?.picture ? `${cdn_url}${item?.user?.picture}` : '/default-profile.png'}
                            sx={{ width: 40, height: 40, border: '2px solid #fff !important' }}
                            title={`${item?.user?.name ?? ''} ${item?.user?.lastname ?? ''}`.trim()}>
                            {GetInitials(item?.user?.name, item?.user?.lastname)}
                          </Avatar>
                          {story?.user_id === item?.user_id && (
                            <Box
                              component='img'
                              src='/icons/Star.svg'
                              alt='Star'
                              sx={{
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                width: 12,
                                height: 12,
                                transform: 'translate(10%, -10%)',
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    </Box>

                    {/* Title */}
                    <Typography
                      variant='h6'
                      sx={{
                        fontFamily: 'DM Sans',
                        fontSize: { xs: '16px', sm: '18px' },
                        fontWeight: 500,
                        lineHeight: { xs: '19.2px', sm: '21.6px' },
                        textAlign: 'left',
                        marginTop: '8px',
                        color: extendedPalette.cardHeaderText,
                      }}>
                      {item.title}
                    </Typography>
                  </Box>
                )}
              </Box>
              );
            })}
            </PinterestMasonry>
          </Box>
        ) : (actionSuccessColab || NoColab) && actionSuccess && memoriesLoaded?.length === 0 ? (
          // Empty state when no memories at all
          !isMobile ? (
            !story?.isLocked &&
            (checkPermissions(user?.roles || [], 'CLIENT_MEMORY_CREATE', story?.id) || user?.id === story?.user_id) ? (
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  padding: '1rem 16px 0px 40px',
                  gap: '1.5rem',
                }}>
                <Typography variant='h6' sx={{ fontSize: '20px', color: 'white', zIndex: 2 }}>
                  Memories
                </Typography>
                <Paper
                  onClick={goToCreateMemory}
                  sx={{
                    borderRadius: 2,
                    overflow: 'hidden',
                    backgroundColor: '#FEFEFE40',
                    color: extendedPalette.cardMediaColor,
                    display: 'flex',
                    flexDirection: 'column',
                    border: '1px dashed rgba(217, 217, 217, 1)',
                    cursor: 'pointer',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '320px',
                    width: '230px',
                    boxShadow: 3,
                  }}>
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <IconButton
                      sx={{
                        width: 35,
                        height: 35,
                        backgroundColor: 'white',
                        '&:hover': { backgroundColor: extendedPalette.cardIconHoverColor },
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        borderRadius: '50%',
                        boxShadow: 3,
                      }}>
                      <Image src={'/icons/add.svg'} width={20} height={20} quality={80} alt={'add'} />
                    </IconButton>
                    <Typography
                      variant='body2'
                      fontSize={20}
                      align='center'
                      sx={{ fontFamily: 'DM Sans', fontWeight: 400, marginTop: 1 }}>
                      Add your <br />
                      first memory
                    </Typography>
                  </Box>
                </Paper>
              </Box>
            ) : null
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '7rem' }}>
              <Paper
                onClick={goToCreateMemory}
                sx={{
                  borderRadius: 2,
                  overflow: 'hidden',
                  backgroundColor: 'rgba(254, 254, 254, 0.25)',
                  color: extendedPalette.cardMediaColor,
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  cursor: 'pointer',
                  height: '100px',
                  width: '350px',
                  padding: '10px',
                  zIndex: 5,
                }}>
                <Typography variant='body2' sx={{ fontFamily: 'DM Sans', fontWeight: 500, marginLeft: 2 }}>
                  {t('first_memory')}
                </Typography>
                <IconButton
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: 'white',
                    '&:hover': { backgroundColor: extendedPalette.cardIconHoverColor },
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: '50%',
                    boxShadow: 3,
                  }}>
                  <Image src={'/icons/add.svg'} width={20} height={20} quality={80} alt={'add'} />
                </IconButton>
              </Paper>
            </Box>
          )
        ) : (
          // Loading state
          <Box width={'100%'} height={'50vh'} display={'flex'} justifyContent={'center'} alignItems={'center'}>
            <CircularProgress sx={{ color: '#fff' }} />
          </Box>
        )}

        {/* Load More / Paywall CTA */}
        {!allItemsLoaded &&
          (!showFour ? (
            <Box
              sx={{
                width: '100%',
                mb: '1rem',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginTop: 2,
                marginBottom: '1rem',
              }}>
              <Box ref={loadMoreButtonRef} component="span">
                <MuiButton
                  minWidth={'10rem'}
                  width={'10rem'}
                  type='button'
                  loading={false}
                  variant={'contained'}
                  sx={extendedPalette.hasCustomTheme ? {
                    background: `${extendedPalette.buttonbackgroundIcon} !important`,
                    '&:hover': { background: `${extendedPalette.buttonbackgroundIcon} !important` }
                  } : {}}
                  method={AllowHandleLoadMore}>
                  <Typography variant='button' color={palette.white}>
                    {t('load_more')}
                  </Typography>
                </MuiButton>
              </Box>
            </Box>
          ) : (
            !modalOpen && (
              <Box
                sx={{
                  position: 'fixed',
                  bottom: isMobile ? '114px' : -3,
                  left: 'center',
                  width: '100%',
                  maxWidth: '1200px',
                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                  color: 'white',
                  p: 4,
                  textAlign: 'center',
                  zIndex: 9999,
                  pointerEvents: 'none',
                }}>
                <Image src={'/images/logo-new-white.svg'} alt={'logo'} width={40} height={21} quality={80} />
                <Typography fontSize={'20px'}>
                  Join {extras?.extraContent?.subscriber_count ?? '3333'} other subscribers & unlock full access to this
                  Memvy.
                </Typography>
                <MuiButton
                  method={() => setModalOpen(true)}
                  variant='filled'
                  width={'auto'}
                  borderRadius='24px'
                  backgroundColor='white'
                  sx={{ mt: 3, borderColor: 'white', color: 'black', '&:hover': { backgroundColor: 'white' }, pointerEvents: 'auto' }}>
                  Get access for ${extras?.extraContent?.price}
                </MuiButton>
              </Box>
            )
          ))}

        {/* Modals */}
        <PopupModal
          open={modalOpen}
          onClose={(nextStep: any) => {
            if (nextStep === 2) {
              setStep(nextStep);
              if (user?.id) dispatch(getUserPurchases(user.id));
              setModalOpen(false);
            } else {
              setModalOpen(false);
            }
          }}
        />

        <MemoryDetail
          open={Boolean(selectedMedia) || openMemoryModal}
          extendedPalette={extendedPalette}
          isLocked={Boolean(story?.isLocked)}
          onClose={closeMemory}
          mediaContent={selectedMedia as any}
          method={switchDeleteMemory}
        />

        <DeleteMemoryModal
          extendedPalette={extendedPalette}
          open={deleteStatusMemory}
          onClose={switchDeleteMemory}
          confirmMethod={deleteMemory}
        />

        <Box
          sx={{
            width: '100%',
            mb: '1rem',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: 2,
            marginBottom: '6rem',
          }}
        />
      </Box>
    </>
  );
};
const MediaGridMemo = React.memo(MediaGrid);
MediaGridMemo.displayName = 'MediaGrid';
export default MediaGridMemo;
